import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { SidebarService } from '../../services/sidebar.service';
import { AuthService } from '../../services/auth.service';
import { catchError, Subject, take, takeUntil, throwError } from 'rxjs';
import { User } from '../../models/auth.model';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { CalendlyUserCredentials } from '../../models/job-stepper.model';
import { environment } from '../../../environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { CalendlyService } from '../../services/calendly.service';

@Component({
  selector: 'app-schedules',
  imports: [SidebarComponent, CommonModule, FormsModule, MatProgressSpinnerModule, TranslateModule],
  templateUrl: './schedules.component.html',
  styleUrl: './schedules.component.scss'
})
export class SchedulesComponent implements OnInit, AfterViewInit, OnDestroy {

  inputUrl: string = '';
  calendlyUrl: string | null = null;
  hasCalendlyUrl: boolean = false;
  isLoading: boolean = true;
  showSaveBtn: boolean = true;

  tokenType: string | null = null;
  calendlyAccessToken: string | null = null;
  calendlyRefreshToken: string | null = null;
  calendlyExpiresAt: number | null = null;

  userData: any = null;
  activeUser: User | null = null;

  userUri: string | null = null;
  errorMessage: string = '';
  scheduledEvents: any = null;

  externalCalendarUrl: string = "";
  isSavingExternalUrl: boolean = false;
  externalUrlSaved: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private _authService: AuthService,
    private _calendlyService: CalendlyService,
    private route: ActivatedRoute,
    private router: Router,
    private sidebarService: SidebarService
  ) {}

  ngOnInit(): void {
    // If redirected from Calendly (first-time OAuth)
    this.route.queryParams.subscribe(params => {
      const code = params['code'];
      if (code) {
        this.handleAuthorizationCode(code);
      }
    });

    // Load user's stored Calendly credentials
    this._authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data && data.calendlyDetails) {
          this.activeUser = data;

          if (data.calendlyDetails.accessToken) {
            this.calendlyAccessToken = data.calendlyDetails.accessToken;
            this.calendlyRefreshToken = data.calendlyDetails.refreshToken;
            this.calendlyExpiresAt = data.calendlyDetails.tokenExpiresAt ? Number(data.calendlyDetails.tokenExpiresAt) : null;
            this.tokenType = "Bearer";

            this.tryLoadCalendlySession();
          }
        }
      });
  }

  toggleMobileMenu() {
    this.sidebarService.toggleMobileMenu();
  }

  ngAfterViewInit(): void {
    this._authService.getUserData()
      .pipe(take(1))
      .subscribe((data: User) => {
        if (data && data.calendlyProfileUrl) {
          this.hasCalendlyUrl = true;
          this.calendlyUrl = data.calendlyProfileUrl;
          this.inputUrl = data.calendlyProfileUrl;
          this.showSaveBtn = false;
        }
        this.isLoading = false;
      });
  }

  // ✅ 1. Handle OAuth Authorization Code Exchange
  handleAuthorizationCode(code: string) {
    this._calendlyService.exchangeAuthorizationCode(code)
      .pipe(catchError(err => throwError(() => err)))
      .subscribe({
        next: data => {
          this.tokenType = data.tokenType;
          this.calendlyAccessToken = data.accessToken;
          this.calendlyRefreshToken = data.refreshToken || null;
          this.calendlyExpiresAt = Date.now() + (data.expiresIn || 3600) * 1000;
          
          // Clear URL params
          this.router.navigate([], {
            queryParams: {},
            replaceUrl: true
          });

          this.saveCalendlyCredentials(data, code);
          this.getCalendlyPublicUrl();
        },
        error: err => console.error('Token exchange error:', err)
      });
  }

  // ✅ 2. Load Existing Session (from DB)
  tryLoadCalendlySession() {
    if (!this.calendlyRefreshToken || !this.calendlyExpiresAt || !this.calendlyAccessToken) {
      this.isLoading = false;
      return;
    }

    const now = Date.now();

    if (now < this.calendlyExpiresAt) {
      this.getCalendlyPublicUrl();
    } else {
      this.refreshAccessToken(this.calendlyRefreshToken);
    }
  }

  // ✅ 3. Refresh Calendly Access Token
  refreshAccessToken(refreshToken: string) {
    this._calendlyService.refreshAccessToken(refreshToken)
      .pipe(catchError(err => throwError(() => err)))
      .subscribe({
        next: data => {
          // Update local values
          this.tokenType = data.tokenType;
          this.calendlyAccessToken = data.accessToken;
          this.calendlyRefreshToken = data.refreshToken || refreshToken;
          this.calendlyExpiresAt = Date.now() + (data.expiresIn || 3600) * 1000;

          // Save updated tokens in DB
          this.saveCalendlyCredentials(data, null);

          this.getCalendlyPublicUrl();
        },
        error: err => console.error("Error refreshing token:", err)
      });
  }

  // ✅ 4. Save Calendly credentials to backend
  saveCalendlyCredentials(data: any, code: string | null) {
    const credentials: CalendlyUserCredentials = {
      calendlyAccessToken: data.accessToken,
      calendlyRefreshToken: data.refreshToken ?? this.calendlyRefreshToken,
      calendlyClientId: environment.calendlyClientId,
      calendlyClientSecret: null,
      calendlyCode: code,
      calendlyRedirectUri: environment.calendlyRedirectUri,
      CalendlyTokenExpiresAt: data.expiresIn
        ? (Date.now() + data.expiresIn * 1000).toString()
        : this.calendlyExpiresAt?.toString() || null
    };

    this._authService.saveCalendlyCredentials(credentials)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.calendlyAccessToken = data.accessToken;
          this.calendlyRefreshToken = data.refreshToken ?? this.calendlyRefreshToken;
          this.calendlyExpiresAt = data.expiresIn ? (Date.now() + data.expiresIn * 1000) : this.calendlyExpiresAt;
          this.tokenType = "Bearer";
          this._authService.getUserData().pipe(take(1)).subscribe(() => {
            this.tryLoadCalendlySession();
          });
        },
        error: err => console.error("Error saving Calendly credentials:", err)
      });
  }

  // ✅ 5. Get Calendly User Info
  getCalendlyPublicUrl(): void {
    if (!this.calendlyAccessToken) return;

    this._calendlyService.getCalendlyUser(this.calendlyAccessToken)
      .pipe(catchError(err => throwError(() => err)))
      .subscribe({
        next: data => {
          this.userData = data;
          this.userUri = data.resource.uri;
          this.getScheduledEvents();
        },
        error: err => console.error("Error fetching user:", err)
      });
  }

  // ✅ 6. Fetch Scheduled Events
  getScheduledEvents(): void {
    if (!this.calendlyAccessToken || !this.userUri) return;

    this._calendlyService.getScheduledEvents(this.calendlyAccessToken, this.userUri)
      .pipe(catchError(err => throwError(() => err)))
      .subscribe({
        next: data => this.scheduledEvents = data,
        error: err => console.error("Error fetching events:", err)
      });
  }

  // ✅ Save profile URL
  saveUrl() {
    if (!this.inputUrl) return alert('Please enter a valid URL');

    this.isLoading = true;

    this._authService.saveCalendlyUrl(this.inputUrl)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.calendlyUrl = this.inputUrl;
          this.showSaveBtn = false;
          this.isLoading = false;
        }
      });
  }

  calendlyLogin() {
    this._calendlyService.initiateOAuthLogin();
  }

  saveExternalCalendarUrl(url: string) {
    if (!url) return;

    this.isSavingExternalUrl = true;
    this.externalUrlSaved = false;

    this._authService.saveExternalCalendarUrl(url)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isSavingExternalUrl = false;
          this.externalUrlSaved = true;
          this._authService.getUserData()
            .pipe(take(1))
            .subscribe({
              next: (user) => {
                this._authService.currentUserSubject.next(user);
              },
              error: (err) => console.error('Failed to refresh user data', err)
            });
        },
        error: () => {
          this.isSavingExternalUrl = false;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}